/**
 * Vendors leaderboard — Stage 5.
 * Sources `/api/bi/vendors/leaderboard/`.
 *
 * Click any row to drill into POs / GRNs and Product Sales for that vendor
 * (see {@link VendorDetailDialogRoot}). The search box filters server-side
 * by name / company / email / phone.
 */
import { useEffect, useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { ErrorState } from '@/components/ui/ErrorState';
import { DataView } from '@/components/data/DataView';
import { KPICard } from '@/components/charts/KPICard';
import { useVendorLeaderboard, type VendorLeaderRow } from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { openVendorDetail } from '@/state/vendorDetailDialog';
import { fmt } from '@/lib/format';

export function VendorsPage() {
  const range = useFilters((s) => s.range);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const query = useVendorLeaderboard({
    start: startISO,
    end: endISO,
    limit: 200,
    q: debouncedSearch || undefined,
  });

  const totals = query.data?.totals;
  const columns = useMemo<ColumnDef<VendorLeaderRow, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Vendor',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{row.original.name}</div>
            {row.original.company ? (
              <div className="truncate text-[11px] text-muted-foreground">
                {row.original.company}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'value',
        header: () => <div className="text-right">Received value</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm font-medium">
            {fmt.npr(row.original.received_value)}
          </div>
        ),
      },
      {
        id: 'fill',
        header: () => <div className="text-right">Fill rate</div>,
        cell: ({ row }) => {
          const r = row.original.fill_rate;
          if (r == null) return <div className="text-right text-xs text-muted-foreground">—</div>;
          const tone = r >= 0.95 ? 'success' : r >= 0.8 ? 'info' : r >= 0.6 ? 'warning' : 'danger';
          return (
            <div className="text-right">
              <Badge tone={tone}>{fmt.pct(r)}</Badge>
            </div>
          );
        },
      },
      {
        id: 'lead',
        header: () => <div className="text-right">Lead (days)</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">
            {row.original.avg_lead_days != null ? row.original.avg_lead_days.toFixed(1) : '—'}
          </div>
        ),
      },
      {
        id: 'pos',
        header: 'Open POs',
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5 text-xs">
            <span className="kpi-num text-foreground">{fmt.number(row.original.open_pos)}</span>
            {row.original.late_pos > 0 ? (
              <Badge tone="danger">{row.original.late_pos} late</Badge>
            ) : null}
          </div>
        ),
      },
      {
        id: 'returns',
        header: () => <div className="text-right">Returns</div>,
        cell: ({ row }) => {
          const rate = row.original.return_rate;
          return (
            <div className="text-right">
              <span className="kpi-num text-sm">{fmt.npr(row.original.returns_value)}</span>
              {rate != null ? (
                <div className="text-[10px] text-muted-foreground">{fmt.pct(rate)}</div>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'grns',
        header: () => <div className="text-right">GRNs</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.number(row.original.grns)}</div>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        eyebrow="Supply"
        title="Vendor leaderboard"
        description="Who shows up, who slows us down. Search a vendor or click a row to drill into POs, GRNs and product sales."
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard label="Vendors active" value={fmt.number(totals?.vendors ?? 0)} loading={query.isLoading} />
        <KPICard label="Received value" value={fmt.npr(totals?.received_value ?? 0)} loading={query.isLoading} />
        <KPICard label="Open POs" value={fmt.number(totals?.open_pos ?? 0)} loading={query.isLoading} />
        <KPICard
          label="Late POs"
          value={fmt.number(totals?.late_pos ?? 0)}
          goodWhenUp={false}
          loading={query.isLoading}
        />
      </div>

      <div className="mt-4">
        {query.isError ? (
          <ErrorState description="Couldn't load vendor leaderboard." onRetry={() => void query.refetch()} />
        ) : (
          <DataView<VendorLeaderRow>
            title="Top vendors"
            description="Click a vendor to open POs, GRNs and product sales."
            data={query.data?.rows ?? []}
            columns={columns}
            isLoading={query.isLoading}
            emptyTitle={debouncedSearch ? 'No vendors match your search' : 'No vendor activity in this window'}
            search={{
              value: search,
              onChange: setSearch,
              placeholder: 'Search vendor by name, company, email…',
            }}
            onRowClick={(row) =>
              openVendorDetail({
                vendorId: row.vendor_id,
                vendorName: row.name,
                vendorCompany: row.company,
              })
            }
          />
        )}
      </div>
    </>
  );
}
